import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Target, Eye, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import SmartLink from "@/components/SmartLink";

const money = (v) => (v == null ? "Not listed" : `$${Number(v).toLocaleString()}`);
const tierStyle = {
  HOME: "bg-primary/10 text-primary border-primary/40",
  READY: "bg-[#7A9B76]/15 text-[#5f7f5b] border-[#7A9B76]/50",
  EXPANSION: "bg-[#D9A441]/15 text-[#9a7423] border-[#D9A441]/50",
};
const healthDot = { ok: "bg-[#7A9B76] animate-pulse", degraded: "bg-[#D9A441]", error: "bg-[#C4674A]", pending: "bg-muted-foreground" };
const sourceLabel = { "sam.gov": "SAM.gov", dc_ocp: "DC OCP", md_emma: "MD eMMA", va_eva: "VA eVA", watchlist: "Watchlist", email: "Email" };

function BidCard({ bid, onDecide }) {
  return (
    <div data-testid={`bid-card-${bid.id}`} className="soft-card p-5 rise-in">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-mono font-bold border px-1.5 py-0.5 ${tierStyle[bid.tier] || ""}`}>{bid.tier}</span>
            <span className="overline-label">{sourceLabel[bid.source] || bid.source}</span>
          </div>
          <p className="text-sm font-bold leading-snug">{bid.title}</p>
          <p className="text-xs text-muted-foreground mt-1 truncate">{bid.agency}</p>
        </div>
        <div className="text-center shrink-0">
          <p className={`font-mono text-2xl font-bold ${bid.score >= 70 ? "text-primary" : bid.score >= 45 ? "text-[#B58530]" : "text-muted-foreground"}`}>{bid.score}</p>
          <p className="overline-label">Fit</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground font-mono flex-wrap">
        <span>Due: {bid.due_date || "—"}</span>
        <span>{money(bid.value)}</span>
        {bid.state && <span>{bid.state}</span>}
        {bid.url && (
          <SmartLink testId={`bid-link-${bid.id}`} href={bid.url} className="text-primary underline">
            {bid.url.startsWith("/") ? "View email" : "Original posting"}
          </SmartLink>
        )}
      </div>
      {bid.matched_keywords?.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {bid.matched_keywords.map((k) => (
            <span key={k} className="text-[10px] bg-secondary border border-border px-1.5 py-0.5 font-mono">{k}</span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <button data-testid={`pursue-bid-${bid.id}`} onClick={() => onDecide(bid, "pursue")} className="h-11 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"><Target size={13} />Pursue</button>
        <button data-testid={`watch-bid-${bid.id}`} onClick={() => onDecide(bid, "watch")} className="h-11 border border-border text-foreground text-xs font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"><Eye size={13} />Watch</button>
        <button data-testid={`pass-bid-${bid.id}`} onClick={() => onDecide(bid, "pass")} className="h-11 border border-border text-muted-foreground text-xs font-bold uppercase tracking-widest tap-btn flex items-center justify-center gap-1"><XCircle size={13} />Pass</button>
      </div>
    </div>
  );
}

export default function Bids() {
  const [tab, setTab] = useState("queue");
  const [bids, setBids] = useState([]);
  const [filter, setFilter] = useState("new");
  const [health, setHealth] = useState([]);
  const [insights, setInsights] = useState(null);
  const [winloss, setWinloss] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = useCallback(() => {
    api.get(`/bids?status=${filter}`).then((r) => setBids(r.data));
    api.get("/sources/health").then((r) => setHealth(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "insights") {
      api.get("/bids/insights").then((r) => setInsights(r.data));
      api.get("/pipeline/analytics").then((r) => setWinloss(r.data));
    }
  }, [tab]);

  const decide = async (bid, decision) => {
    try {
      const { data } = await api.post(`/bids/${bid.id}/decision`, { decision });
      if (decision === "pursue") toast.success("Added to pipeline — first follow-up due in 48h");
      else toast(decision === "watch" ? "Watching" : "Passed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const poll = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/sources/poll");
      toast.success(`Poll complete — ${data.new_matches} new match${data.new_matches === 1 ? "" : "es"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPolling(false); }
  };

  return (
    <div data-testid="bids-page">
      <div className="px-5 pt-8 pb-4 flex items-end justify-between">
        <div>
          <p className="overline-label text-primary">Sourcing Engine</p>
          <h1 className="font-display text-4xl tracking-tight text-primary">Bids</h1>
        </div>
        <button data-testid="poll-sources-button" onClick={poll} disabled={polling} className="h-11 px-4 border border-primary text-primary text-xs font-bold uppercase tracking-widest tap-btn flex items-center gap-2 disabled:opacity-50">
          <RefreshCw size={13} className={polling ? "animate-spin" : ""} />{polling ? "Polling" : "Poll now"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 px-4 pb-2">
        {[["queue", "Queue"], ["sources", "Sources"], ["insights", "Insights"]].map(([k, l]) => (
          <button key={k} data-testid={`bids-tab-${k}`} onClick={() => setTab(k)}
            className={`h-11 text-xs font-bold uppercase tracking-widest tap-btn rounded-xl ${tab === k ? "bg-primary text-primary-foreground shadow-sm" : "bg-card text-muted-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "queue" && (
        <div className="p-4 space-y-3">
          <div className="flex gap-1">
            {["new", "watch", "pursue", "pass", "expired"].map((s) => (
              <button key={s} data-testid={`bid-filter-${s}`} onClick={() => setFilter(s)}
                className={`px-3 h-9 text-[10px] font-bold uppercase tracking-widest border tap-btn ${filter === s ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                {s}
              </button>
            ))}
          </div>
          {bids.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No {filter} bids. Hit "Poll now" to check all sources.</p>}
          {bids.map((b) => (filter === "new" || filter === "watch" ? (
            <BidCard key={b.id} bid={b} onDecide={decide} />
          ) : (
            <div key={b.id} className="soft-card p-4 flex items-center justify-between gap-2">
              <p className="text-sm truncate">{b.title}</p>
              <span className="font-mono text-sm text-muted-foreground shrink-0">{b.score}</span>
            </div>
          )))}
        </div>
      )}

      {tab === "sources" && (
        <div className="p-4 space-y-2" data-testid="source-health-list">
          {health.map((h) => (
            <div key={h.source} data-testid={`source-health-${h.source}`} className="soft-card p-4 flex items-center gap-3">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${healthDot[h.status] || healthDot.pending}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold">{h.label}</p>
                <p className="text-xs text-muted-foreground truncate">{h.message}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-xs uppercase">{h.status}</p>
                <p className="font-mono text-[10px] text-muted-foreground">{h.last_run ? new Date(h.last_run).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "never"}</p>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground pt-2">Sources run independently — one failing never takes down the others. All poll daily at 5:30 AM ET.</p>
        </div>
      )}

      {tab === "insights" && insights && (
        <div className="p-4 space-y-5" data-testid="insights-panel">
          {winloss && winloss.overall.won + winloss.overall.lost > 0 && (
            <div data-testid="winloss-section">
              <p className="overline-label mb-2">Pipeline Win / Loss</p>
              <div className="grid grid-cols-3 soft-card divide-x divide-border/60 text-center overflow-hidden">
                <div className="py-4">
                  <p className="font-mono text-2xl font-bold text-primary" data-testid="win-rate-value">{winloss.overall.win_rate}%</p>
                  <p className="overline-label">Win rate</p>
                </div>
                <div className="py-4">
                  <p className="font-mono text-2xl font-bold text-[#5f7f5b]">{winloss.overall.won}</p>
                  <p className="overline-label">Won</p>
                </div>
                <div className="py-4">
                  <p className="font-mono text-2xl font-bold text-muted-foreground">{winloss.overall.lost}</p>
                  <p className="overline-label">Lost</p>
                </div>
              </div>
              {winloss.overall.won_value > 0 && (
                <p className="text-xs text-muted-foreground mt-1.5">Won value: <span className="font-mono text-[#5f7f5b]">${Number(winloss.overall.won_value).toLocaleString()}</span></p>
              )}
              {[["By Source", winloss.by_source], ["By Vertical", winloss.by_vertical], ["By Client Type", winloss.by_client_type]].map(([label, rows]) => (
                rows.length > 0 && (
                  <div key={label} className="mt-3">
                    <p className="overline-label mb-1.5">{label}</p>
                    {rows.map((r) => (
                      <div key={r.label} className="flex items-center gap-2 py-1.5 border-b border-border text-xs">
                        <span className="w-32 truncate">{r.label}</span>
                        <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-2.5 bg-[#7A9B76]" style={{ width: `${r.win_rate}%` }} /></div>
                        <span className="font-mono w-24 text-right">{r.win_rate}% ({r.won}W/{r.lost}L)</span>
                      </div>
                    ))}
                  </div>
                )
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Based on {insights.total_decided} decisions. Use this to tune your fit profile.</p>
          <div>
            <p className="overline-label mb-2">Keywords → Pursue Rate</p>
            {insights.keywords.filter((k) => k.total > 0).map((k) => (
              <div key={k.keyword} className="flex items-center gap-2 py-1.5 border-b border-border text-xs">
                <span className="w-36 truncate font-mono">{k.keyword}</span>
                <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-2.5 bg-primary" style={{ width: `${k.pursue_rate}%` }} /></div>
                <span className="font-mono w-20 text-right">{k.pursue_rate}% ({k.pursue}/{k.total})</span>
              </div>
            ))}
            {insights.keywords.every((k) => k.total === 0) && <p className="text-xs text-muted-foreground">No decided bids with keyword matches yet.</p>}
          </div>
          <div>
            <p className="overline-label mb-2">Sources → Pursue Rate</p>
            {insights.sources.filter((s) => s.total > 0).map((s) => (
              <div key={s.source} className="flex items-center gap-2 py-1.5 border-b border-border text-xs">
                <span className="w-36 truncate">{s.label}</span>
                <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-2.5 bg-primary" style={{ width: `${s.pursue_rate}%` }} /></div>
                <span className="font-mono w-20 text-right">{s.pursue_rate}% ({s.pursue}/{s.total})</span>
              </div>
            ))}
            {insights.sources.every((s) => s.total === 0) && <p className="text-xs text-muted-foreground">No decisions yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
